/**
 * Helper script to fetch Mews room categories for manual mapping
 * 
 * Run this script to see all available Mews room categories with their IDs and names.
 * Use this output to manually create the room mapping configuration.
 * 
 * Usage: Import and call fetchRoomCategoriesForMapping() from a test page or console.
 */

import { getHotels } from './configuration';
import type { RoomCategory } from './types';

export interface RoomCategoryInfo {
  id: string;
  name: string;
  description?: string;
  ordering: number;
  normalBedCount: number;
  extraBedCount: number;
  spaceType: string;
  allNames: Record<string, string>;
}

/**
 * Fetches all Mews room categories and formats them for manual mapping
 * 
 * @returns Array of room category information
 */
export async function fetchRoomCategoriesForMapping(): Promise<{
  success: boolean;
  roomCategories: RoomCategoryInfo[];
  error?: string;
}> {
  try {
    const response = await getHotels(undefined, false);
    
    if (response.error) {
      return {
        success: false,
        roomCategories: [],
        error: response.error.Message || 'Failed to fetch hotels data',
      };
    }

    if (!response.data?.RoomCategories || response.data.RoomCategories.length === 0) {
      return {
        success: false,
        roomCategories: [],
        error: 'No room categories found in hotels response',
      };
    }

    const languageCode = response.data.DefaultLanguageCode || 'en-US';
    const roomCategories: RoomCategoryInfo[] = response.data.RoomCategories.map((room: RoomCategory) => {
      const name = room.Name[languageCode] || Object.values(room.Name)[0] || 'Unknown';
      
      return {
        id: room.Id,
        name: name,
        description: room.Description?.[languageCode] || room.Description?.[Object.keys(room.Description || {})[0]] || undefined,
        ordering: room.Ordering,
        normalBedCount: room.NormalBedCount,
        extraBedCount: room.ExtraBedCount,
        spaceType: room.SpaceType,
        allNames: room.Name,
      };
    });

    // Sort by ordering
    roomCategories.sort((a, b) => a.ordering - b.ordering);

    return {
      success: true,
      roomCategories,
    };
  } catch (error) {
    return {
      success: false,
      roomCategories: [],
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Prints room categories in a format suitable for manual mapping
 */
export async function printRoomCategoriesForMapping(): Promise<void> {
  const result = await fetchRoomCategoriesForMapping();
  
  if (!result.success) {
    console.error('❌ Failed to fetch room categories:', result.error);
    return;
  }

  console.log('\n📋 Mews Room Categories Available for Mapping:\n');
  console.log('=' .repeat(80));
  
  result.roomCategories.forEach((room, index) => {
    console.log(`\n${index + 1}. ${room.name}`);
    console.log(`   ID: ${room.id}`);
    if (room.description) {
      console.log(`   Description: ${room.description}`);
    }
    console.log(`   Beds: ${room.normalBedCount} normal, ${room.extraBedCount} extra`);
    console.log(`   Type: ${room.spaceType}`);
    if (Object.keys(room.allNames).length > 1) {
      console.log(`   All Names:`, room.allNames);
    }
  });

  console.log('\n' + '='.repeat(80));
  console.log(`\nTotal: ${result.roomCategories.length} room categories\n`);

  // Also print as JSON for easy copy-paste
  console.log('\n📋 JSON Format (for mapping configuration):\n');
  const mappingTemplate: Record<string, string> = {};
  result.roomCategories.forEach((room) => {
    // Create a placeholder key based on room name (normalized)
    const normalizedName = room.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    mappingTemplate[normalizedName] = room.id;
  });
  
  console.log(JSON.stringify(mappingTemplate, null, 2));
  console.log('\n');
}
